import React from 'react';
import {
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Slider,
  FormControlLabel,
  Checkbox,
  Alert
} from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useSkills } from '../../contexts/SkillsContext';

// Palette de couleurs prédéfinies
const COLOR_PALETTE = [
  { name: 'Bleu', color: '#2196F3' },
  { name: 'Vert', color: '#4CAF50' },
  { name: 'Orange', color: '#FF9800' },
  { name: 'Rouge', color: '#F44336' },
  { name: 'Violet', color: '#9C27B0' },
  { name: 'Teal', color: '#009688' },
  { name: 'Indigo', color: '#3F51B5' },
  { name: 'Rose', color: '#E91E63' },
  { name: 'Cyan', color: '#00BCD4' },
  { name: 'Lime', color: '#CDDC39' },
  { name: 'Amber', color: '#FFC107' },
  { name: 'Deep Orange', color: '#FF5722' },
  { name: 'Blue Grey', color: '#607D8B' },
  { name: 'Brown', color: '#795548' },
  { name: 'Grey', color: '#9E9E9E' }
];

const validationSchema = yup.object({
  name: yup.string()
    .required('Le nom de la compétence est requis')
    .min(2, 'Le nom doit contenir au moins 2 caractères')
    .max(50, 'Le nom ne peut pas dépasser 50 caractères'),
  coverageIndex: yup.number()
    .min(0, 'L\'indice de couverture doit être au moins 0')
    .max(100, 'L\'indice de couverture ne peut pas dépasser 100')
    .required('L\'indice de couverture est requis'),
  color: yup.string()
    .required('La couleur est requise')
});

const SkillForm = ({ skill, onClose, onSuccess }) => {
  const { addSkill, updateSkill } = useSkills();

  const formik = useFormik({
    initialValues: {
      name: skill?.name || '',
      coverageIndex: skill?.coverageIndex || 0,
      color: skill?.color || COLOR_PALETTE[0].color,
      critical: skill?.critical || false
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (skill?.id) {
          await updateSkill(skill.id, values);
        } else {
          await addSkill(values);
        }
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      } catch (error) {
        console.error('Erreur lors de l\'enregistrement de la compétence:', error);
      }
    }
  });

  return (
    <>
      <DialogContent 
        dividers={false}
        sx={{ 
          p: 0,
          '&.MuiDialogContent-root': {
            p: 0
          }
        }}
      >
        <Box
          component="form"
          onSubmit={formik.handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            minWidth: 400,
            p: 3,
            pt: 6,
            '& .MuiFormControl-root': {
              my: 0.5
            }
          }}
        >
          <TextField
            fullWidth
            id="name"
            name="name"
            label="Nom de la compétence"
            value={formik.values.name}
            onChange={formik.handleChange}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
          />

          <Box sx={{ width: '100%' }}>
            <Typography variant="subtitle2" gutterBottom>
              Indice de couverture
            </Typography>
            <TextField
              fullWidth
              id="coverageIndex"
              name="coverageIndex"
              label="Valeur en pourcentage"
              type="number"
              value={formik.values.coverageIndex}
              onChange={formik.handleChange}
              error={formik.touched.coverageIndex && Boolean(formik.errors.coverageIndex)}
              helperText={formik.touched.coverageIndex && formik.errors.coverageIndex}
              InputProps={{ inputProps: { min: 0, max: 100 } }}
              sx={{ mb: 1 }}
            />
            <Slider
              value={formik.values.coverageIndex}
              onChange={(_, value) => formik.setFieldValue('coverageIndex', value)}
              aria-labelledby="coverage-index-slider"
              valueLabelDisplay="auto"
              step={1}
              marks={[
                { value: 0, label: '0%' },
                { value: 25, label: '25%' },
                { value: 50, label: '50%' },
                { value: 75, label: '75%' },
                { value: 100, label: '100%' }
              ]}
              min={0}
              max={100}
            />
          </Box>

          <FormControl fullWidth>
            <InputLabel id="color-label">Couleur</InputLabel>
            <Select
              labelId="color-label"
              id="color"
              name="color"
              value={formik.values.color}
              onChange={formik.handleChange}
              label="Couleur"
            >
              {COLOR_PALETTE.map((colorOption) => (
                <MenuItem 
                  key={colorOption.color} 
                  value={colorOption.color}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                  }}
                >
                  <Box
                    sx={{
                      width: 24,
                      height: 24,
                      backgroundColor: colorOption.color,
                      borderRadius: '4px',
                      border: '1px solid rgba(0, 0, 0, 0.12)'
                    }}
                  />
                  <Typography>
                    {colorOption.name}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  id="critical"
                  name="critical"
                  checked={formik.values.critical}
                  onChange={formik.handleChange}
                  color="error"
                />
              }
              label={
                <Typography>
                  Compétence critique (réservée aux techniciens de niveau 2)
                </Typography>
              }
            />
            {formik.values.critical && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Lors de la planification automatique et assistée, seuls les techniciens ayant un niveau 2 pour cette compétence pourront être affectés.
              </Alert>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={onClose}>
          Annuler
        </Button>
        <Button
          onClick={formik.handleSubmit}
          variant="contained"
          color="primary"
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {skill?.id ? 'Modifier' : 'Ajouter'}
        </Button>
      </DialogActions>
    </>
  );
};

export default SkillForm;